import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Mail, Lock, Eye, EyeOff, MessageSquare, ArrowLeft, CheckCircle } from 'lucide-react';

// SkynetManager 백엔드 프록시를 통해 AntsTalk API 호출
const API_BASE = '';

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  @media(max-width:768px){min-height:calc(100dvh - 56px);padding:1rem;}
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 420px;
  box-sizing:border-box;
  @media(max-width:768px){padding:1.5rem;}
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.4;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 1rem;
  transition: border-color 0.2s;
  &:focus-within {
    border-color: #3b82f6;
  }
`;

const InputIcon = styled.span`
  color: #94a3b8;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  flex: 1;
  border: none;
  padding: 0.75rem 0.5rem;
  font-size: 0.875rem;
  outline: none;
  &::placeholder {
    color: #94a3b8;
  }
`;

const PasswordToggle = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
  &:hover {
    color: #64748b;
  }
`;

const Button = styled.button`
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover:not(:disabled) {
    background-color: #2563eb;
  }
  &:disabled {
    background-color: #93c5fd;
    cursor: not-allowed;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0;
  margin-bottom: 1rem;
  &:hover {
    color: #1e293b;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.8rem;
  text-align: center;
  padding: 0.5rem;
  background-color: #fef2f2;
  border-radius: 4px;
`;

const TimerText = styled.div`
  font-size: 0.8rem;
  color: ${props => props.expired ? '#ef4444' : '#64748b'};
  text-align: center;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const StepDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.active ? '#3b82f6' : '#e2e8f0'};
  transition: background-color 0.2s;
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
`;

const SuccessIcon = styled.div`
  color: #22c55e;
`;

const SuccessText = styled.p`
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
`;

const PasswordRecoveryPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: 계정입력, 2: 인증코드, 3: 새 비밀번호
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [remainingTime, setRemainingTime] = useState(300);
  const [timerActive, setTimerActive] = useState(false);

  // 타이머
  useEffect(() => {
    let interval;
    if (timerActive && remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime(prev => prev - 1);
      }, 1000);
    } else if (remainingTime === 0) {
      setTimerActive(false);
      setError('인증번호가 만료되었습니다. 다시 요청해주세요.');
    }
    return () => clearInterval(interval);
  }, [timerActive, remainingTime]);

  // Step 1: 인증번호 요청
  const handleSendVerification = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('이메일 주소를 입력해주세요');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_account: email })
      });

      const data = await response.json();

      if (data.success) {
        setStep(2);
        setRemainingTime(300);
        setTimerActive(true);
      } else {
        setError(data.message || '인증번호 전송에 실패했습니다');
      }
    } catch (err) {
      console.error('인증 요청 오류:', err);
      setError('서버 연결에 실패했습니다. 나중에 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: 인증번호 확인
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      setError('6자리 인증번호를 입력해주세요');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_account: email,
          code: verificationCode
        })
      });

      const data = await response.json();

      if (data.success) {
        setResetToken(data.reset_token);
        setTimerActive(false);
        setStep(3);
      } else {
        setError(data.message || '인증번호가 올바르지 않습니다');
      }
    } catch (err) {
      console.error('인증 확인 오류:', err);
      setError('서버 연결에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: 비밀번호 변경
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      setError('새 비밀번호를 입력해주세요');
      return;
    }
    if (newPassword.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reset_token: resetToken,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setStep(4); // 성공 화면
      } else {
        setError(data.message || '비밀번호 변경에 실패했습니다');
      }
    } catch (err) {
      console.error('비밀번호 변경 오류:', err);
      setError('서버 연결에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <PageContainer>
      <Card>
        <BackButton onClick={() => step === 1 ? navigate('/login') : setStep(step - 1)}>
          <ArrowLeft size={16} />
          {step === 1 ? '로그인으로 돌아가기' : '이전 단계'}
        </BackButton>

        <StepIndicator>
          <StepDot active={step >= 1} />
          <StepDot active={step >= 2} />
          <StepDot active={step >= 3} />
        </StepIndicator>

        {step === 1 && (
          <>
            <Title>비밀번호 찾기</Title>
            <Subtitle>
              등록된 이메일을 입력하면<br />
              AntsTalk 채팅으로 인증번호를 보내드립니다
            </Subtitle>
            <Form onSubmit={handleSendVerification}>
              <InputGroup>
                <InputIcon><Mail size={18} /></InputIcon>
                <Input
                  type="email"
                  placeholder="이메일 주소"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </InputGroup>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <Button type="submit" disabled={loading}>
                {loading ? '전송 중...' : '인증번호 받기'}
              </Button>
            </Form>
          </>
        )}

        {step === 2 && (
          <>
            <Title>인증번호 입력</Title>
            <Subtitle>
              AntsTalk 채팅으로 전송된<br />
              6자리 인증번호를 입력해주세요
            </Subtitle>
            <Form onSubmit={handleVerifyCode}>
              <InputGroup>
                <InputIcon><MessageSquare size={18} /></InputIcon>
                <Input
                  type="text"
                  placeholder="인증번호 6자리"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  autoFocus
                />
              </InputGroup>
              <TimerText expired={remainingTime < 60}>
                남은 시간: {formatTime(remainingTime)}
              </TimerText>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <Button type="submit" disabled={loading || verificationCode.length !== 6}>
                {loading ? '확인 중...' : '인증 확인'}
              </Button>
              <Button
                type="button"
                style={{ backgroundColor: 'transparent', color: '#3b82f6', border: '1px solid #3b82f6' }}
                onClick={handleSendVerification}
                disabled={loading}
              >
                인증번호 재전송
              </Button>
            </Form>
          </>
        )}

        {step === 3 && (
          <>
            <Title>새 비밀번호 설정</Title>
            <Subtitle>새로운 비밀번호를 입력해주세요</Subtitle>
            <Form onSubmit={handleResetPassword}>
              <InputGroup>
                <InputIcon><Lock size={18} /></InputIcon>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="새 비밀번호"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoFocus
                />
                <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </PasswordToggle>
              </InputGroup>
              <InputGroup>
                <InputIcon><Lock size={18} /></InputIcon>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="비밀번호 확인"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </InputGroup>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <Button type="submit" disabled={loading}>
                {loading ? '변경 중...' : '비밀번호 변경'}
              </Button>
            </Form>
          </>
        )}

        {step === 4 && (
          <SuccessContainer>
            <SuccessIcon><CheckCircle size={64} /></SuccessIcon>
            <SuccessText>비밀번호가 변경되었습니다</SuccessText>
            <Subtitle>새 비밀번호로 로그인해주세요</Subtitle>
            <Button onClick={() => navigate('/login')} style={{ width: '100%' }}>
              로그인 페이지로 이동
            </Button>
          </SuccessContainer>
        )}
      </Card>
    </PageContainer>
  );
};

export default PasswordRecoveryPage;
